import { faAngleDown, faBookOpen, faCode, faCommentDots, faComments, faGlobe, faHiking, faHome, faMap, faMapMarkedAlt, faMicrophone, faMicrophoneSlash, faSave, faSignOutAlt, faStar, faStickyNote, faUser, faUserGroup, faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Box, Button, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, Tab, Tabs, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiContext } from './App';

import Split from 'react-split';

import AutoSaves from './AutoSaves';
import Codex from './CharCodex';
import Inventory from './CharInventory';
import Journal from './CharJournal';
import CharacterOverview from './CharOverview';
import SkillsAndAbilities from './CharSkillsAndAbilities';
import Companions from './Companions';
import Map from './Map';
import Mechanics from './Mechanics';
import Messages from './Messages';
import RecordMessage from './RecordMessage';
import Scene from './Scene';

const GameDisplay = ({ token, setToken }) => {
    // Track the primary tab and secondary tab selections for each of the 3 panels
    const [value, setValue] = useState([[3, 0], [1, 0], [2, 0]]); // For wide view
    const [primaryValue, setPrimaryValue] = useState(1); // For narrow view
    const [secondaryValues, setSecondaryValues] = useState({ 1: 0 }); // Track selected sub-tab per primary category
    const [drawerOpen, setDrawerOpen] = useState(false); // Control drawer visibility on mobile
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    //const isWideScreen = useMediaQuery(theme.breakpoints.up('md')); // Breakpoint for wide view
    const isWideScreen = false; // disable wide screen for now
    const [showPrimaryTabs, setShowPrimaryTabs] = useState(false);
    const [checkpointIdToLoad, setCheckpointIdToLoad] = useState(null);
    const [reloadVersion, setReloadVersion] = useState(0);
    const messageComponentRef = useRef(null)
    const tabHeight = isWideScreen
        ? showPrimaryTabs
            ? '180px'  // widescreen and showPrimaryTabs is true
            : '132px'  // widescreen and showPrimaryTabs is false
        : '80px';      // not widescreen

    const [isRecording, setIsRecording] = useState(false);
    const [isNarratorVoice, setNarratorVoice] = useState(true);
    const { game_id } = useParams();
    const BASE_URL = useContext(ApiContext);
    const [gameResponse, setGameResponse] = useState(null);
    const navigate = useNavigate();

    const handleAutoSaveLoad = (checkpointId) => {
        setCheckpointIdToLoad(checkpointId);
        setReloadVersion(reloadVersion + 1);
    };

    const handleSecondaryTabChange = (category, newValue) => {
        setSecondaryValues((prev) => ({
            ...prev,
            [category]: newValue // Persist selected sub-tab for this category
        }));
    };

    const handleTabChange = (panelIndex, newPrimaryValue) => {
        const updatedValue = [...value];
        updatedValue[panelIndex] = [
            newPrimaryValue,
            secondaryValues[primaryTabs[newPrimaryValue].category] || 0 // Persist sub-tab or default to 0
        ];
        setValue(updatedValue);
    };

    const handleWideSecondaryTabChange = (panelIndex, newSecondaryValue) => {
        const updatedValue = [...value];
        updatedValue[panelIndex][1] = newSecondaryValue;
        // Persist the secondary tab selection for this panel
        const category = primaryTabs[value[panelIndex][0]].category;
        setSecondaryValues((prev) => ({
            ...prev,
            [category]: newSecondaryValue,
        }));
        setValue(updatedValue);
    };

    const toggleDrawer = (open) => {
        setDrawerOpen(open);
    };

    const secondaryTabs = {
        1: [ // Message and Mechanics tabs
            { label: 'Messages', icon: <FontAwesomeIcon icon={faCommentDots} />, component: <Messages ref={messageComponentRef} tabHeight={tabHeight} token={token} game_id={game_id} BASE_URL={BASE_URL} setGameResponse={setGameResponse} isRecording={isRecording} setIsRecording={setIsRecording} isNarratorVoice={isNarratorVoice} setNarratorVoice={setNarratorVoice} isWideScreen={isWideScreen} gameResponse={gameResponse} /> },
            { label: 'Character', icon: <FontAwesomeIcon icon={faUser} />, component: <CharacterOverview character={gameResponse?.character} tabHeight={tabHeight} BASE_URL={BASE_URL} game_id={game_id} token={token} /> },
            { label: 'Scene', icon: <FontAwesomeIcon icon={faMapMarkedAlt} />, component: <Scene current_scene={gameResponse?.scene} scene_image_id={gameResponse?.scene_image_id} tabHeight={tabHeight} BASE_URL={BASE_URL} game_id={game_id} token={token} /> },
            { label: 'Mechanics', icon: <FontAwesomeIcon icon={faCode} />, component: <Mechanics mechanicsContent={gameResponse?.mechanics} tabHeight={tabHeight} /> },
            { label: 'Companions', icon: <FontAwesomeIcon icon={faUserGroup} />, component: <Companions character={gameResponse?.character} tabHeight={tabHeight} /> }
        ],
        2: [ // Character tabs

            { label: 'Skills & Abilities', icon: <FontAwesomeIcon icon={faStar} />, component: <SkillsAndAbilities character={gameResponse?.character} tabHeight={tabHeight} /> },
            { label: 'Inventory', icon: <FontAwesomeIcon icon={faHiking} />, component: <Inventory character={gameResponse?.character} tabHeight={tabHeight} /> },
            { label: 'Codex', icon: <FontAwesomeIcon icon={faBookOpen} />, component: <Codex character={gameResponse?.character} tabHeight={tabHeight} /> },
            { label: 'Journal', icon: <FontAwesomeIcon icon={faStickyNote} />, component: <Journal character={gameResponse?.character} tabHeight={tabHeight} /> },

        ],
        3: [ // Scene and World tabs
            { label: 'Area Map', icon: <FontAwesomeIcon icon={faGlobe} />, component: <Map tabHeight={tabHeight} token={token} game_id={game_id} BASE_URL={BASE_URL} checkpointId={gameResponse?.checkpoint_id} /> }
        ],
        4: [ // Menu and Saves
            { label: 'Auto Saves', icon: <FontAwesomeIcon icon={faSave} />, component: <AutoSaves checkpointsContent={gameResponse?.checkpoints} handleAutoSaveLoad={handleAutoSaveLoad} tabHeight={tabHeight} /> }
        ],
    };


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const gameEndpoint = checkpointIdToLoad
                    ? `${BASE_URL}/v1/game/${game_id}/${checkpointIdToLoad}`
                    : `${BASE_URL}/v1/game/${game_id}`;

                const response = await fetch(gameEndpoint, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const game_response = await response.json();
                setGameResponse(game_response);

                // Jump to the Messages tab
                setTimeout(() => {
                    setPrimaryValue(1);
                    setSecondaryValues({ 1: 0 });
                }, 0);

            } catch (error) {
                console.error("Error fetching game data:", error);
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [BASE_URL, game_id, token, checkpointIdToLoad, reloadVersion]);

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', marginTop: 4 }}>
                <Typography color="error">Error: {error.message}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: (theme) => theme.palette.background.default,
                color: (theme) => theme.palette.text.primary,
            }}>
                <img src="/loading.gif"
                    alt="loading.gif"
                    style={{ height: '100px', width: 'auto' }} />
                <Typography>Loading...</Typography>
            </Box>
        );
    }



    const handleRecordClickForRecordMessage = () => {
        if (messageComponentRef.current) {
            messageComponentRef.current.handleRecordClick();  // Trigger recording in Messages
        }
    };



    const primaryTabs = [
        { label: 'Record', icon: <FontAwesomeIcon icon={faMicrophone} />, component: <RecordMessage tabHeight={tabHeight} handleRecordClick={handleRecordClickForRecordMessage} isRecording={isRecording} /> },
        { label: 'Messaging', icon: <FontAwesomeIcon icon={faComments} />, category: 1 },
        { label: 'Character', icon: <FontAwesomeIcon icon={faUser} />, category: 2 },
        { label: 'Scene & World', icon: <FontAwesomeIcon icon={faMap} />, category: 3 },
        { label: 'Menu', icon: <FontAwesomeIcon icon={faSave} />, category: 4 }
    ];




    return (
        <Box sx={{
            height: '100vh', width: '100%', backgroundColor: (theme) => theme.palette.background.default,
            color: (theme) => theme.palette.text.primary,
        }}>
            {!isWideScreen ? (
                <>
                    {/* Menu button in the top left, toggles drawer open/close */}
                    <IconButton
                        sx={{ position: 'fixed', top: '10px', left: '40px', transform: 'translateX(-50%)', color: '#1876d2', marginLeft: '0px', zIndex: 1300 }}
                        onClick={() => toggleDrawer(!drawerOpen)}
                    >
                        <MenuIcon />
                    </IconButton>

                    <IconButton
                        sx={{ position: 'fixed', top: '10px', left: '50%', transform: 'translateX(-50%)', color: '#1876d2', marginLeft: '0px', zIndex: 100 }}
                        onClick={handleRecordClickForRecordMessage}
                    >
                        <FontAwesomeIcon className="blue-icon" icon={isRecording ? faMicrophoneSlash : faMicrophone} />
                    </IconButton>

                    <IconButton
                        sx={{ position: 'fixed', top: '10px', right: '5px', transform: 'translateX(-50%)', color: '#1876d2', marginLeft: '0px', zIndex: 100 }}
                        onClick={() => setNarratorVoice(!isNarratorVoice)}
                    >
                        <FontAwesomeIcon className="blue-icon" icon={isNarratorVoice ? faVolumeMute : faVolumeUp} />
                    </IconButton>


                    {/* Drawer showing only secondary tabs */}
                    <Drawer anchor="left" open={drawerOpen} onClose={() => toggleDrawer(false)}>
                        <Box sx={{ width: 250, mt: '50px' }}>
                            <List sx={{ paddingX: 1.5, paddingTop: 0, paddingBottom: 0 }}>
                                {Object.entries(secondaryTabs).map(([category, tabs]) => (

                                    tabs.map((subTab, subIndex) => (
                                        <ListItem
                                            sx={{ py: 0.5 }}
                                            button="true"
                                            key={`${category}-${subIndex}`}
                                            onClick={() => {
                                                setPrimaryValue(category);
                                                handleSecondaryTabChange(category, subIndex);
                                                toggleDrawer(false); // Close drawer after selection
                                            }}
                                        >
                                            <ListItemIcon>
                                                {subTab.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={subTab.label} />
                                        </ListItem>
                                    ))
                                ))}


                                <ListItem
                                    sx={{ py: 0.25 }}
                                    button="true"
                                    onClick={() => {
                                        navigate('/');
                                    }}
                                >
                                    <ListItemIcon><FontAwesomeIcon icon={faHome} /></ListItemIcon>
                                    <ListItemText primary="Main Menu" />
                                </ListItem>


                            </List>
                        </Box>
                    </Drawer>

                    {/* Display selected secondary tab content */}
                    <Box sx={{ paddingTop: '60px' }}>

                        <Box sx={{
                            overflowY: 'auto',
                            paddingX: 1
                        }}>
                            {/* Always mount Messages component but conditionally show it */}
                            <Box sx={{ display: primaryValue === 1 && secondaryValues[1] === 0 ? 'block' : 'none' }}>
                                <Messages
                                    ref={messageComponentRef}
                                    tabHeight={tabHeight}
                                    game_id={game_id}
                                    token={token}
                                    BASE_URL={BASE_URL}
                                    setGameResponse={setGameResponse}
                                    isRecording={isRecording}
                                    setIsRecording={setIsRecording}
                                    isNarratorVoice={isNarratorVoice}
                                    setNarratorVoice={setNarratorVoice}
                                    isWideScreen={isWideScreen}
                                    gameResponse={gameResponse}
                                />
                            </Box>

                            {/* Conditionally show other selected components */}
                            {(primaryValue !== 1 || secondaryValues[1] !== 0) && (
                                secondaryTabs[primaryTabs[primaryValue].category]?.[secondaryValues[primaryTabs[primaryValue].category]]?.component
                            )}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',     // Fix the position at the bottom
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            maxHeight: '20px',  // Set your desired max height here
                            height: '20px',     // Set a fixed height for consistent scaling
                            overflow: 'hidden',
                            padding: '0',       // Optional padding for spacing
                        }}
                    >
                        <img
                            src="/combo-1.png"
                            alt="gamemaster.chat logo"
                            style={{ height: '100%', width: 'auto' }}  // Constrain the image within the box
                        />
                    </Box>
                </>
            ) : (

                <>
                    {/* widescreen header */}
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '5px',
                            height: '45px', // Adjusted height for header
                            boxSizing: 'border-box',
                        }}
                    >
                        {/* Logo on the left */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <img
                                src="/combo-1.png"
                                alt="gamemaster.chat logo"
                                style={{ height: '30px', width: 'auto' }}
                            />
                        </Box>

                        {/* Buttons on the right */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                startIcon={<FontAwesomeIcon icon={faHome} />}
                                onClick={() => navigate('/')}
                            >
                                Main Menu
                            </Button>

                            <Button
                                startIcon={<FontAwesomeIcon icon={faSignOutAlt} />}
                                onClick={() => {
                                    setToken(null);
                                    localStorage.removeItem('token');
                                    navigate('/');
                                }}
                            >
                                Logout
                            </Button>
                        </Box>
                    </Box>

                    <Split
                        sizes={[25, 50, 25]}
                        minSize={100}
                        direction="horizontal"
                        style={{ display: 'flex', height: 'calc(100vh - 100px)' }} // Adjust height to subtract header
                        gutterSize={3}
                        gutter={(index, direction) => {
                            const gutter = document.createElement('div');
                            gutter.className = `gutter gutter-${direction}`;
                            gutter.style.cursor = 'col-resize';
                            return gutter;
                        }}
                    >
                        {/* Each of the 3 panels */}
                        {Array.from({ length: 3 }).map((_, panelIndex) => (
                            <Box key={panelIndex} sx={{ height: '100%' }}> {/* Ensure panel uses full available height */}
                                {/* Content with auto overflow */}
                                <Box
                                    sx={{
                                        padding: 2,
                                        maxHeight: `calc(100vh - ${tabHeight})`, // Subtract header and tab heights
                                        overflowY: 'auto',
                                    }}
                                >
                                    {primaryTabs[value[panelIndex][0]].category !== undefined
                                        ? secondaryTabs[primaryTabs[value[panelIndex][0]].category][value[panelIndex][1]].component
                                        : primaryTabs[value[panelIndex][0]].component}
                                </Box>

                                {/* Render secondary tabs if applicable */}
                                {primaryTabs[value[panelIndex][0]].category !== undefined && (
                                    <Tabs
                                        value={value[panelIndex][1]}
                                        onChange={(event, newValue) => {
                                            if (newValue === 'toggle-primary-tabs') {
                                                setShowPrimaryTabs((prev) => !prev);
                                            } else {
                                                handleWideSecondaryTabChange(panelIndex, newValue);
                                            }
                                        }}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        orientation="horizontal"
                                    >
                                        {secondaryTabs[primaryTabs[value[panelIndex][0]].category].map((subTab, subIndex) => (
                                            <Tab icon={subTab.icon} key={subIndex} sx={{ minWidth: 50, height: 30, padding: '0px' }} />
                                        ))}
                                        <Tab
                                            icon={<FontAwesomeIcon icon={faAngleDown} />}
                                            key="toggle-primary-tabs"
                                            sx={{ minWidth: 50, height: 30, padding: '0px' }}
                                            value="toggle-primary-tabs"
                                        />
                                    </Tabs>
                                )}

                                {/* Render primary tabs if toggled on */}
                                {showPrimaryTabs && (
                                    <Tabs
                                        value={value[panelIndex][0]}
                                        onChange={(event, newValue) => handleTabChange(panelIndex, newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        orientation="horizontal"
                                    >
                                        {primaryTabs.map((tab, index) => (
                                            <Tab icon={tab.icon} key={index} sx={{ minWidth: 50, height: 30, padding: '0px' }} />
                                        ))}
                                    </Tabs>
                                )}
                            </Box>
                        ))}
                    </Split>
                </>

            )
            }
        </Box >
    );
};

export default GameDisplay;
